import React from "react";
import * as XLSX from "sheetjs-style";

/** Festiva teal — matches report UI / brand for Excel headers */
export const HEADER_FILL = { patternType: "solid", fgColor: { rgb: "008080" } };
export const HEADER_FONT = {
  bold: true,
  color: { rgb: "FFFFFF" },
  name: "Calibri",
  sz: 11,
};
export const TITLE_FILL = { patternType: "solid", fgColor: { rgb: "004D4D" } };
export const TITLE_FONT = {
  bold: true,
  color: { rgb: "FFFFFF" },
  name: "Calibri",
  sz: 14,
};
export const META_FILL = { patternType: "solid", fgColor: { rgb: "E6F4F4" } };
export const META_FONT = {
  bold: true,
  color: { rgb: "004D4D" },
  name: "Calibri",
  sz: 10,
};

export const formatDate = (value, empty = "—") => {
  if (!value) return empty;
  const parsed = new Date(String(value).split("T")[0]);
  return Number.isNaN(parsed.getTime())
    ? String(value).split("T")[0]
    : parsed.toLocaleDateString();
};

export const formatMoney = (amount, currency = "INR", empty = "—") => {
  if (amount == null || amount === "" || Number.isNaN(Number(amount))) {
    return empty;
  }
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: currency || "INR",
      maximumFractionDigits: 2,
    }).format(Number(amount));
  } catch {
    return `${currency || ""} ${Number(amount).toFixed(2)}`.trim();
  }
};

export const formatSpendList = (list = [], empty = "—") => {
  if (!list.length) return empty;
  return list
    .map((row) => formatMoney(row.amount, row.currency, ""))
    .filter(Boolean)
    .join(" · ");
};

export const yesNo = (value) => (value ? "Yes" : "No");

export const safeFileLabel = (value, fallback = "Event") =>
  String(value || fallback)
    .replace(/[\\/:*?"<>|]/g, "")
    .trim() || fallback;

const styleCell = (worksheet, row, col, style) => {
  const ref = XLSX.utils.encode_cell({ r: row, c: col });
  if (!worksheet[ref]) {
    worksheet[ref] = { t: "s", v: "" };
  }
  worksheet[ref].s = style;
};

export const applyHeaderStyle = (worksheet, headerRow = 0) => {
  if (!worksheet["!ref"]) return;
  const range = XLSX.utils.decode_range(worksheet["!ref"]);
  for (let col = range.s.c; col <= range.e.c; col += 1) {
    styleCell(worksheet, headerRow, col, {
      fill: HEADER_FILL,
      font: HEADER_FONT,
      alignment: {
        horizontal: "center",
        vertical: "center",
        wrapText: true,
      },
      border: {
        top: { style: "thin", color: { rgb: "006666" } },
        bottom: { style: "thin", color: { rgb: "006666" } },
        left: { style: "thin", color: { rgb: "006666" } },
        right: { style: "thin", color: { rgb: "006666" } },
      },
    });
  }
  if (!worksheet["!rows"]) worksheet["!rows"] = [];
  worksheet["!rows"][headerRow] = { hpt: 24 };
};

/**
 * Export tabular report data for event operations teams.
 * Adds title + event meta rows, teal header styling, freeze + autofilter.
 */
export const exportRowsToExcel = ({
  rows,
  columns,
  sheetName,
  fileName,
  reportTitle,
  eventName,
}) => {
  const title = reportTitle || sheetName || "Report";
  const eventLabel = eventName || "";
  const exportedAt = new Date().toLocaleString();
  const colCount = columns.length + 1; // Sr No.

  const headers = ["Sr No.", ...columns.map((col) => col.label)];
  const dataRows = rows.map((row, index) =>
    columns.map((col) => {
      const raw = col.value(row, index);
      if (raw == null) return "";
      return raw;
    })
  );

  const aoa = [
    [title, ...Array(Math.max(0, colCount - 1)).fill("")],
    [
      eventLabel ? `Event: ${eventLabel}` : "Event: —",
      `Exported: ${exportedAt}`,
      ...Array(Math.max(0, colCount - 2)).fill(""),
    ],
    headers,
    ...dataRows.map((values, index) => [index + 1, ...values]),
  ];

  const workbook = XLSX.utils.book_new();
  const worksheet = XLSX.utils.aoa_to_sheet(aoa);

  const lastCol = colCount - 1;
  const lastRow = aoa.length - 1;
  worksheet["!ref"] = XLSX.utils.encode_range({
    s: { r: 0, c: 0 },
    e: { r: lastRow, c: lastCol },
  });

  if (lastCol > 0) {
    worksheet["!merges"] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: lastCol } },
    ];
  }

  for (let col = 0; col <= lastCol; col += 1) {
    styleCell(worksheet, 0, col, {
      fill: TITLE_FILL,
      font: TITLE_FONT,
      alignment: { horizontal: "left", vertical: "center" },
    });
    styleCell(worksheet, 1, col, {
      fill: META_FILL,
      font: META_FONT,
      alignment: { horizontal: "left", vertical: "center" },
    });
  }

  applyHeaderStyle(worksheet, 2);

  worksheet["!rows"] = worksheet["!rows"] || [];
  worksheet["!rows"][0] = { hpt: 28 };
  worksheet["!rows"][1] = { hpt: 20 };
  worksheet["!rows"][2] = { hpt: 24 };

  dataRows.forEach((values, rowIdx) => {
    const excelRow = 3 + rowIdx;
    let lineCount = 1;
    values.forEach((value, colIdx) => {
      const col = columns[colIdx];
      if (!col?.wrap) return;
      const text = value == null ? "" : String(value);
      lineCount = Math.max(lineCount, text.split("\n").length);
      styleCell(worksheet, excelRow, colIdx + 1, {
        alignment: { vertical: "top", wrapText: true, horizontal: "left" },
        font: { name: "Calibri", sz: 10 },
      });
    });
    worksheet["!rows"][excelRow] = {
      hpt: Math.min(140, Math.max(18, lineCount * 13 + 6)),
    };
  });

  worksheet["!views"] = [
    {
      state: "frozen",
      xSplit: 0,
      ySplit: 3,
      topLeftCell: "A4",
      activePane: "bottomLeft",
    },
  ];
  worksheet["!autofilter"] = {
    ref: XLSX.utils.encode_range({
      s: { r: 2, c: 0 },
      e: { r: lastRow, c: lastCol },
    }),
  };

  worksheet["!cols"] = [
    { wch: 8 },
    ...columns.map((col) => ({ wch: col.width || 18 })),
  ];

  XLSX.utils.book_append_sheet(workbook, worksheet, String(sheetName).slice(0, 31));
  XLSX.writeFile(workbook, fileName);
};

export const MetricCard = ({
  label,
  value,
  hint,
  tone,
  className = "",
  style,
}) => (
  <article
    className={`stay-report-metric ${tone || ""} ${className}`.trim()}
    style={style}
  >
    <span className="stay-report-metric-label">{label}</span>
    <strong className="stay-report-metric-value">{value}</strong>
    {hint ? <em className="stay-report-metric-hint">{hint}</em> : null}
  </article>
);

export const ReportPageShell = ({
  title,
  subtitle,
  loading,
  hasEvent,
  onRefresh,
  onExport,
  exportDisabled,
  metrics,
  toolbar,
  children,
}) => {
  if (!hasEvent) {
    return (
      <div className="container-fluid stay-report-page festiva-report-page">
        <div className="occasion-top occasion-top-with-sidebar row g-4 align-items-center">
          <div className="col-lg-9">
            <h2 className="mb-0">{title}</h2>
          </div>
        </div>
        <div className="stay-report-empty-state">
          <p>Select an event to view this report.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid stay-report-page festiva-report-page">
      <div className="occasion-top occasion-top-with-sidebar row g-4 align-items-center">
        <div className="col-12 stay-report-title-row">
          <div>
            <h2 className="mb-0">{title}</h2>
            {subtitle ? <p className="festiva-report-subtitle">{subtitle}</p> : null}
          </div>
          <div className="stay-report-title-actions">
            <button
              type="button"
              className="btn stay-report-action-btn stay-report-icon-btn"
              onClick={onRefresh}
              disabled={loading}
              title="Refresh"
              aria-label="Refresh"
            >
              <i className="bi bi-arrow-clockwise" aria-hidden="true" />
            </button>
            {onExport ? (
              <button
                type="button"
                className="btn stay-report-action-btn stay-report-export-btn"
                onClick={onExport}
                disabled={exportDisabled || loading}
              >
                Export to Excel
                <i className="bi bi-download" aria-hidden="true" />
              </button>
            ) : null}
          </div>
        </div>
      </div>

      {loading && !children ? (
        <p className="stay-report-loading">Loading report…</p>
      ) : (
        <>
          {metrics?.length ? (
            <section
              className={`stay-report-metrics${
                metrics.length >= 6
                  ? " stay-report-metrics-6"
                  : metrics.length === 4
                    ? " stay-report-metrics-4"
                    : metrics.length === 3
                      ? " stay-report-metrics-3"
                      : metrics.length === 2
                        ? " stay-report-metrics-2"
                        : metrics.length === 1
                          ? " stay-report-metrics-1"
                          : ""
              }`}
              aria-label="Summary metrics"
            >
              {metrics.map((metric, index) => (
                <MetricCard
                  key={metric.key || metric.label || index}
                  {...metric}
                />
              ))}
            </section>
          ) : null}
          {toolbar}
          {children}
        </>
      )}
    </div>
  );
};
